import { Badge, Card, CardBody, DataTable, GroupByPanel, MultiSelect, Icon, Modal } from "@aidkitorg/component-library";
import { FieldOption, GroupByConfig } from "@aidkitorg/types/lib/admin-panel/filters";
import { DocumentLibraryItem } from "@aidkitorg/types/lib/convoai/documentLibrary.types";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { usePost } from "../../API";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { snakeToEnglish } from "../../Util";
import { PreviewDocument } from "./DocumentLibrary";


export function DocumentEvalOverview() {

  type DocumentEvaluation = NonNullable<Awaited<ReturnType<typeof getDocumentScores>>['data']>[number];

  const getDocumentScores = usePost('/ai/document_library/scores');
  const [evaluations, setEvaluations] = useState<DocumentEvaluation[]>([]);
  const [previewDocument, setPreviewDocument] = useState<DocumentLibraryItem | null>(null);

  useAsyncEffect(async () => {

    const scores = await getDocumentScores({});

    if (!scores.data || scores.error) {
      return;
    }

    setEvaluations(scores.data);

  }, []);

  function determineLatencyBand(millis: number) {
    if (millis < 1_000) {
      return 'Very Low';
    }
    if (millis < 4_000) {
      return 'Low';
    }
    if (millis < 10_000) {
      return 'Medium';
    }
    if (millis < 60_000) {
      return 'High';
    }
    
    return 'Very High';
  }

  function ListCell<T>({ items, render, keyFn, legend, showCount }: T extends string ? {
    items: T[],
    render?: (t: T) => React.ReactNode
    keyFn?: (t: T) => string,
    legend?: React.ReactNode,
    showCount?: number
  } : {
    items: T[],
    render: (t: T) => React.ReactNode,
    keyFn: (t: T) => string,
    legend?: React.ReactNode,
    showCount?: number
  }) {

    const [useShowCount, setUseShowCount] = useState<boolean>(typeof showCount === 'number');

    if (!items.length) {
      return <span className="text-gray-400">None</span>
    }

    return <div
      onMouseOver={() => setUseShowCount(false)}
      onMouseLeave={() => setUseShowCount(true)}
    >
      <ul className="space-y-1 list-inside border rounded-md p-2">
        <li hidden={!legend} className="border-b border-gray-300 px-0.5 text-center">{legend}</li>
        {
          (useShowCount ? items.slice(0, showCount) : items).map(item =>
            <li key={keyFn?.(item) ?? item} className="">{render?.(item) ?? item}</li>
          )
        }
        <li hidden={!useShowCount || (!showCount || showCount >= items.length)}>
          <span>{items.length - (showCount || 0)} more</span>
        </li>
      </ul>
    </div>
  }

  const fieldToStrFn = ({ name }: DocumentEvaluation['fieldResults'][number]) => snakeToEnglish(name);
  const numFormatter = new Intl.NumberFormat(undefined, {
    unit: 'millisecond',
    compactDisplay: 'long',
    useGrouping: true
  });

  const columnDefs: ColumnDef<DocumentEvaluation>[] = useMemo(() => [
    {
      id: 'docId',
      header: 'Doc ID',
      accessorKey: 'document.id'
    },
    {
      id: 'category',
      header: 'Category',
      accessorKey: 'document.category',
      cell: ({ row, renderValue }) => {

        if (row.getIsGrouped()) {
          return <div>
            {renderValue() as any}
            GROUPED
          </div>
        }

        return renderValue();
      }
    },
    {
      id: 'valid',
      header: 'Valid',
      accessorFn: ({ document: { valid } }) => valid ? 'Valid' : 'Invalid',
      cell: ({ row: { original: { document } } }) => <Badge variant={document.valid ? 'success' : 'error'}>{document.valid ? 'Valid' : 'Invalid'}</Badge>
    },
    {
      id: 'variations',
      header: 'Attributes',
      cell: ({ row: { original: { document } } }) =>
        <ListCell
          render={variations => <span className="text-xs">{variations}</span>}
          items={document.metadata?.variations ?? []}
          showCount={1}
        />
    },
    {
      id: 'correct',
      header: 'Correct',
      cell: ({ row: { original: { fieldResults } } }) =>
        <ListCell
          showCount={3}
          keyFn={fieldToStrFn}
          render={field => <span className="text-xs">{field.name}</span>}
          items={fieldResults
            .filter(result => result.actual === result.expected)}
        />
    },
    {
      id: 'errors',
      header: 'Errors',
      accessorFn: ({ fieldResults }) => fieldResults.filter(({ expected, actual }) => expected !== actual).length,
      cell: ({ row: { original: { fieldResults } } }) =>
        <ListCell
          showCount={3}
          legend={<span className="text-xs text-gray-400">{'Document <-> Response'}</span>}
          keyFn={fieldToStrFn}
          render={({ name, expected, actual }) =>
            <span className="text-xs"><strong>{snakeToEnglish(name)}</strong>:&nbsp;"{expected ?? ''}" != "{actual ?? ''}"</span>}
          items={fieldResults
            .filter(result => result.actual !== result.expected)}
        />
    },
    {
      id: 'template',
      header: 'Template Version',
      enableResizing: true,
      accessorFn: ({ template }) => `${template.name} v${template.version ?? 0}`
    },
    {
      id: 'model',
      accessorFn: ({ model: { name, params } }) => params?.reasoning ? `${name}-${params.reasoning}` : name,
      header: 'LLM Version',
    },
    {
      id: 'passed',
      header: 'Pass/Fail',
      accessorFn: ({ passed }) => passed ? 'Pass' : 'Fail',
      cell: ({ row: { original: { passed } } }) => <Badge variant={passed ? 'success' : 'error'}>{passed ? 'Pass' : 'Fail'}</Badge>
    },
    {
      id: 'latency',
      header: 'Latency',
      accessorKey: 'requestDurationMillis',
      cell: ({ row: { original: { requestDurationMillis } } }) => <span>{numFormatter.format(requestDurationMillis)}&nbsp;ms</span>
    },
    {
      id: 'latencyBand',
      header: 'Latency Band',
      accessorFn: ({ requestDurationMillis }) => determineLatencyBand(requestDurationMillis),
      cell: ({ row: { original: { requestDurationMillis } } }) => <span>{determineLatencyBand(requestDurationMillis)}</span>
    }
  ], []);

  const [visibleFieldOptions, setVisibleFieldOptions] = useState<{ label: string | JSX.Element, value: string }[]>([]);
  const [hiddenFields, setHiddenFields] = useState<string[]>([]);

  const fieldOptions = useMemo<FieldOption[]>(() => [
    {
      label: 'Model',
      key: 'model',
      type: 'text'
    },
    {
      label: 'Validity',
      key: 'valid',
      type: 'boolean'
    },
    {
      label: 'Template',
      key: 'template',
      type: 'text'
    },
    {
      label: 'Category',
      key: 'category',
      type: 'text'
    },
    {
      label: 'Pass/Fail',
      key: 'passed',
      type: 'boolean'
    },
    {
      label: 'Latency Band',
      key: 'latencyBand',
      type: 'number'
    }
  ], []);

  const [groupByConfig, setGroupByConfig] = useState<GroupByConfig>({
    enabled: true,
    rules: []
  });

  useEffect(() => {
    const hideByDefault = ['docId', 'correct'];
    const defs = columnDefs
      .filter(def => !hideByDefault.includes(def.id!))
      .map(def => ({
        label: def.id!,
        value: def.id!
      }));

    setVisibleFieldOptions(defs);
  }, [columnDefs]);

  useEffect(() => {

    const toShow = new Set(visibleFieldOptions.map(({ value }) => value));
    const toHide = new Set(groupByConfig.rules?.map(({ field }) => field) ?? []);

    for (const def of columnDefs) {

      if (!def.id) {
        console.warn('id of column left blank. check your definitions!', def);
        continue;
      };

      if (!toShow.has(def.id)) {
        toHide.add(def.id);
      }
    }

    setHiddenFields(Array.from(toHide));
  }, [visibleFieldOptions, groupByConfig]);

  return <div className="flex-col p-2">
    <h4>Document Score Review</h4>
    <Card>
      <CardBody>
        <div className="inline-flex flex-row gap-2">
          <MultiSelect
            label="Visible Columns"
            labelLocation="inline"
            hideLabel
            anchor="bottom end"
            className="transition-colors duration-300 text-left flex-grow rounded-md ring-[1px] ring-gray-300 bg-[#FFFFFF] hover:bg-gray-50"
            customButtonTextRenderer={() => (
              <div className="flex items-center gap-2">
                <Icon name="ViewColumnsIcon" type="solid" />
                <span className="font-bold">Columns</span>
                <span className="border-l-[1px] border-gray-200 pl-2 font-bold">{columnDefs.length - hiddenFields.length}</span>
              </div>
            )}
            options={
              columnDefs
                .filter(def => !groupByConfig.rules || !groupByConfig.rules.some(r => def.id === r.field))
                .map(opt => ({
                  label: opt.id!,
                  value: opt.id!
                }))}
            value={visibleFieldOptions}
            onChange={setVisibleFieldOptions}
            description="Column visibility controls"
          />
          <GroupByPanel
            availableFields={fieldOptions}
            value={groupByConfig}
            onConfirm={setGroupByConfig}
          />
        </div>
      </CardBody>
    </Card>
    <div className="overflow-x-scroll">
      <DataTable
        resizableColumns
        enableColumnReordering
        enableColumnVisibility
        groupBy={groupByConfig}
        columns={columnDefs}
        data={evaluations}
        hiddenCols={hiddenFields}
        actions={[
          {
            label: 'Preview',
            onClick(item) {
              setPreviewDocument(item.document);
            }
          },
        ]}
      />
    </div>
    {/* Preview Modal */}
    {previewDocument && <Modal
      open={!!previewDocument}
      onClose={() => setPreviewDocument(null)}
      title={`Document Preview - ${previewDocument?.original_filename}`}
      size="lg"
    >
      <PreviewDocument
        document={previewDocument}
        onClose={() => setPreviewDocument(null)}
      />
    </Modal>}
  </div>
}
